import React from "react";
import styled from "styled-components";
import { CreateReleaseButton } from "ui-kit/Button";

interface FooterProps {
  nextButtonText: React.ReactNode;
  // Some call sites pass react-final-form `handleSubmit`, which returns a Promise<AnyObject|undefined>
  // and also accepts an optional event param. We always invoke it with no args.
  onNext: () => any;
  disabled?: boolean;
  
}

export const Footer: React.FC<FooterProps> = props => {
  const { nextButtonText, disabled, onNext,  } = props;
  const [loading, setLoading] = React.useState(false);
  const isMountedRef = React.useRef(true);

  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleNext = async () => {
    if (disabled || loading) return;
    setLoading(true);
    try {
      await Promise.resolve(onNext());
      // If navigation happens, component unmounts and loading resets naturally.
    } catch (e) {
      // If save failed and we stayed on the page, restore text.
      if (isMountedRef.current) setLoading(false);
      throw e;
    } finally {
      // If we stayed on the same page (e.g. Add track to list), ensure we restore the button.
      // If navigation occurred, the component will unmount and this won't run (or will be ignored).
      if (isMountedRef.current) setLoading(false);
    }
  };

  return (
    <Container>
      <NextButton
        type="button"
        onMouseDown={() => {
          // keep existing interaction pattern but make it async-safe
          handleNext();
        }}
        disabled={disabled || loading}
        aria-busy={loading}
      >
        <ButtonText $hidden={loading}>
        {nextButtonText}
        </ButtonText>
        <ButtonSpinnerWrap $visible={loading} aria-hidden={!loading}>
          <ButtonSpinner />
        </ButtonSpinnerWrap>
      </NextButton>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  flex: 1;
  /* Only the actual button should show pointer / trigger hover tooltips */
  cursor: default;
  position: relative;
  z-index: 5;
  background: transparent;
`;

const NextButton = styled(CreateReleaseButton)`
  min-height: 47px;
  border-radius: 12px;
  position: relative;
  z-index: 4;
  cursor: pointer;

  /* remove PrimeReact focus ring / blue outline */
  &:focus,
  &:focus-visible,
  &.p-button:focus,
  &.p-button:focus-visible {
    outline: none !important;
    box-shadow: none !important;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    filter: none;
    transform: none;
    animation-play-state: paused;
  }
`;

const ButtonText = styled.span<{ $hidden: boolean }>`
  visibility: ${(p) => (p.$hidden ? "hidden" : "visible")};
`;

const ButtonSpinnerWrap = styled.span<{ $visible: boolean }>`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  pointer-events: none;
`;

const ButtonSpinner = styled.span`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: rgba(255, 255, 255, 0.95);
  display: inline-block;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
